import { usePlaygroundClient, useUserClient } from '@/lib/api'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useRef, useState } from 'react'

interface TemplateInfo {
  relativePath: string
  brand: string
  customer: string | null
  templateType: string
  extension: string
}

interface GroupedTemplates {
  [templateType: string]: {
    [brandOrCustomer: string]: TemplateInfo
  }
}

function groupTemplates(templates: TemplateInfo[]): GroupedTemplates {
  const grouped: GroupedTemplates = {}
  for (const t of templates) {
    if (!grouped[t.templateType]) {
      grouped[t.templateType] = {}
    }
    const label = t.customer ? `${t.brand}/${t.customer}` : t.brand
    grouped[t.templateType][label] = t
  }
  return grouped
}

export default function PlaygroundPage() {
  const client = usePlaygroundClient()
  const userClient = useUserClient()

  const { data: userResponse, isLoading: loadingUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => await userClient.getMe.query(),
  })

  const user = useMemo(
    () => ({ user: userResponse?.body as any }),
    [userResponse?.body],
  )

  const isSuperadmin = user?.user?.role === 'Superadmin'

  const [selectedPath, setSelectedPath] = useState<string | null>(null)
  const [previewHtml, setPreviewHtml] = useState<string>('')
  const [imagePlaceholders, setImagePlaceholders] = useState<string[]>([])
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null)
  const [incentiveDataUrl, setIncentiveDataUrl] = useState<string | null>(null)
  const [notificationDataUrl, setNotificationDataUrl] = useState<string | null>(null)
  const logoInputRef = useRef<HTMLInputElement>(null)
  const incentiveInputRef = useRef<HTMLInputElement>(null)
  const notificationInputRef = useRef<HTMLInputElement>(null)

  const { data: templatesResponse, isLoading } = useQuery({
    queryKey: ['playgroundTemplates'],
    queryFn: async () => await client.listTemplates.query(),
  })

  const renderMutation = useMutation({
    mutationFn: (relativePath: string) =>
      client.renderTemplate.mutate({ body: { relativePath } }),
    onSuccess: (response) => {
      if (response.status === 200) {
        setImagePlaceholders(response.body.imagePlaceholders)
        setPreviewHtml(
          replaceImagePlaceholders(
            response.body.html,
            logoDataUrl,
            incentiveDataUrl,
            notificationDataUrl,
          ),
        )
      }
    },
  })

  const handleSelectTemplate = useCallback(
    (path: string) => {
      setSelectedPath(path)
      renderMutation.mutate(path)
    },
    [renderMutation],
  )

  const handleFileUpload = useCallback(
    (
      event: React.ChangeEvent<HTMLInputElement>,
      setter: (url: string | null) => void,
      type: 'logo' | 'incentive' | 'notification',
    ) => {
      const file = event.target.files?.[0]
      if (!file) return

      const reader = new FileReader()
      reader.onload = () => {
        const dataUrl = reader.result as string
        setter(dataUrl)

        // Re-render with current template if one is selected
        if (selectedPath) {
          renderMutation.mutate(selectedPath, {
            onSuccess: (response) => {
              if (response.status === 200) {
                setImagePlaceholders(response.body.imagePlaceholders)
                const newLogo = type === 'logo' ? dataUrl : logoDataUrl
                const newIncentive =
                  type === 'incentive' ? dataUrl : incentiveDataUrl
                const newNotification =
                  type === 'notification' ? dataUrl : notificationDataUrl
                setPreviewHtml(
                  replaceImagePlaceholders(
                    response.body.html,
                    newLogo,
                    newIncentive,
                    newNotification,
                  ),
                )
              }
            },
          })
        }
      }
      reader.readAsDataURL(file)
    },
    [selectedPath, logoDataUrl, incentiveDataUrl, notificationDataUrl, renderMutation],
  )

  const templates =
    templatesResponse?.status === 200
      ? (templatesResponse.body as TemplateInfo[])
      : []
  const grouped = groupTemplates(templates)

  if (isLoading || loadingUser) return <div className="p-8">Loading...</div>

  if (!isSuperadmin) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        Access restricted to Superadmin users.
      </div>
    )
  }

  return (
    <div className="flex h-full bg-gray-50">
      {/* Sidebar */}
      <div className="w-72 border-r bg-white overflow-y-auto flex-shrink-0">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">Email Templates</h2>
          <p className="text-sm text-gray-500 mt-1">
            {templates.length} templates
          </p>
        </div>
        <div className="p-2">
          {Object.entries(grouped)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([templateType, variants]) => (
              <div key={templateType} className="mb-3">
                <div className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {templateType}
                </div>
                {Object.entries(variants)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([label, info]) => (
                    <button
                      key={info.relativePath}
                      onClick={() => handleSelectTemplate(info.relativePath)}
                      className={`w-full text-left px-3 py-1.5 text-sm rounded transition-colors ${
                        selectedPath === info.relativePath
                          ? 'bg-blue-100 text-blue-800 font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {label}
                      <span className="ml-1 text-xs text-gray-400">
                        {info.extension}
                      </span>
                    </button>
                  ))}
              </div>
            ))}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Toolbar */}
        <div className="p-4 border-b bg-white flex items-center gap-6 flex-shrink-0">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Logo:</label>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleFileUpload(e, setLogoDataUrl, 'logo')}
              className="text-sm file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            {logoDataUrl && (
              <button
                onClick={() => {
                  setLogoDataUrl(null)
                  if (logoInputRef.current) logoInputRef.current.value = ''
                  if (selectedPath) handleSelectTemplate(selectedPath)
                }}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">
              Incentive Image:
            </label>
            <input
              ref={incentiveInputRef}
              type="file"
              accept="image/*"
              onChange={(e) =>
                handleFileUpload(e, setIncentiveDataUrl, 'incentive')
              }
              className="text-sm file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            {incentiveDataUrl && (
              <button
                onClick={() => {
                  setIncentiveDataUrl(null)
                  if (incentiveInputRef.current)
                    incentiveInputRef.current.value = ''
                  if (selectedPath) handleSelectTemplate(selectedPath)
                }}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">
              Reminder Image:
            </label>
            <input
              ref={notificationInputRef}
              type="file"
              accept="image/*"
              onChange={(e) =>
                handleFileUpload(e, setNotificationDataUrl, 'notification')
              }
              className="text-sm file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            {notificationDataUrl && (
              <button
                onClick={() => {
                  setNotificationDataUrl(null)
                  if (notificationInputRef.current)
                    notificationInputRef.current.value = ''
                  if (selectedPath) handleSelectTemplate(selectedPath)
                }}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
          {imagePlaceholders.length > 0 && (
            <div className="ml-auto flex items-center gap-1">
              <span className="text-xs text-gray-500">Placeholders:</span>
              {imagePlaceholders.map((p) => (
                <span
                  key={p}
                  className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded"
                >
                  {p}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Preview */}
        <div className="flex-1 p-4 overflow-auto">
          {selectedPath ? (
            renderMutation.isPending ? (
              <div className="flex items-center justify-center h-full text-gray-500">
                Rendering...
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border overflow-hidden h-full">
                <iframe
                  srcDoc={previewHtml}
                  className="w-full h-full border-0"
                  title="Email preview"
                  sandbox="allow-same-origin"
                />
              </div>
            )
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400">
              Select a template from the sidebar to preview
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function replaceImagePlaceholders(
  html: string,
  logoUrl: string | null,
  incentiveUrl: string | null,
  notificationUrl: string | null,
): string {
  let result = html

  if (logoUrl) {
    result = result
      .replace(/cid:logo/g, logoUrl)
      .replace(/\{\{logo\}\}/g, logoUrl)
  }

  if (incentiveUrl) {
    result = result
      .replace(/cid:incentive-picture/g, incentiveUrl)
      .replace(/\{\{gift_image\}\}/g, incentiveUrl)
  }

  const notificationReplacement = notificationUrl
    ? `<img src="${notificationUrl}" style="display:block; width:100%; max-width:600px; height:auto;" alt="notification image" />`
    : ''
  result = result.replace(/\{\{notification_image\}\}/g, notificationReplacement)

  return result
}
